// Inbox — Gmail threads, live. We store nothing — every list/open hits Gmail
// through the backend proxy. TanStack Query caches per-thread payloads so
// re-opening doesn't re-hit. `threadId` here is Gmail's providerThreadId.
// If no mailbox is connected we surface a CTA pointing at Settings.
//
// UI is Notion-style: roomy typography, soft surfaces, hover lift on rows,
// avatar initials per thread, attachment chips inline with each message.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Loader2, AlertCircle, Mail, MailOpen, Reply, RefreshCcw, Search,
  ArrowDownLeft, ArrowUpRight, Forward, ReplyAll,
  Paperclip, Download, FileText, MailMinus,
  Inbox as InboxIcon, Send, Star, AlertTriangle, Trash2, Archive, FileEdit,
} from "lucide-react";
import toast from "react-hot-toast";
import { Select as AntSelect } from "antd";
import { Button } from "../atoms";
import PageHeader from "../organisms/PageHeader";
import {
  useEmailAccounts, useEmailThreads, useEmailThread, useManualSync,
  useMarkThreadRead, useMarkThreadStarred, downloadAttachment,
  useMailboxStats, useModifyThread,
} from "../hooks/useEmails";
import EmailComposeModal from "../overlays/EmailComposeModal";
import { getRecipientLines } from "../utils/emailDisplayUtils";

const PAGE_SIZE = 20;

// Gmail system folders we expose. `labelId` is what the backend passes to
// Gmail's listMessages; `null` means "no label filter" (All mail).
// `direction` flips the row's primary identity field — Sent shows recipients
// instead of senders, etc.
const FOLDERS = [
  { id: "INBOX",     label: "Inbox",     icon: InboxIcon,     direction: "incoming" },
  { id: "STARRED",   label: "Starred",   icon: Star,          direction: "incoming" },
  { id: "SENT",      label: "Sent",      icon: Send,          direction: "outgoing" },
  { id: "DRAFT",     label: "Drafts",    icon: FileEdit,      direction: "outgoing" },
  { id: "ARCHIVE",   label: "Archive",   icon: Archive,       direction: "incoming" },
  { id: "IMPORTANT", label: "Important", icon: AlertTriangle, direction: "incoming" },
  { id: "SPAM",      label: "Spam",      icon: AlertCircle,   direction: "incoming" },
  { id: "TRASH",     label: "Trash",     icon: Trash2,        direction: "incoming" },
  { id: "ALL",       label: "All mail",  icon: Mail,          direction: "incoming" },
];

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "";
  const date = new Date(d);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  if (sameDay) return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const sameYear = date.getFullYear() === today.getFullYear();
  return date.toLocaleDateString(undefined,
    sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" }
  );
}

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Request failed";
}

// Initials from a name or email — same convention as the rest of CRM.
function initialsOf(s) {
  const str = String(s || "?");
  // If it's an email, use the local part
  const local = str.includes("@") ? str.split("@")[0] : str;
  return local
    .split(/[\s._-]+/).filter(Boolean)
    .slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
}

// Deterministic soft color per email/string. Avoids picking from a fixed
// palette so two adjacent rows rarely collide.
function tintFor(s) {
  let h = 0;
  for (let i = 0; i < String(s).length; i++) h = (h * 31 + String(s).charCodeAt(i)) % 360;
  return `hsl(${h}, 70%, 92%)`;
}
function inkFor(s) {
  let h = 0;
  for (let i = 0; i < String(s).length; i++) h = (h * 31 + String(s).charCodeAt(i)) % 360;
  return `hsl(${h}, 55%, 38%)`;
}

// ── empty states ─────────────────────────────────────────────────────────────

function ConnectMailboxCTA() {
  return (
    <div style={{ padding: 64, textAlign: "center", color: "var(--color-text-secondary)" }}>
      <div style={{
        width: 56, height: 56, borderRadius: 8,
        margin: "0 auto 14px",
        background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <Mail size={26} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--color-text-primary)", marginBottom: 6 }}>
        Connect a mailbox to see your inbox
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.55, maxWidth: 420, margin: "0 auto 16px" }}>
        Connect Gmail to read and send email directly from your inbox — just like Gmail.
      </div>
      <div style={{ fontSize: 12 }}>
        Go to <strong>Settings → Email</strong> to connect.
      </div>
    </div>
  );
}

// ── folder rail ──────────────────────────────────────────────────────────────

function FolderRail({ value, onChange, onCompose, inboxUnread }) {
  return (
    <nav style={{
      width: 200, flexShrink: 0,
      borderRight: "1px solid var(--color-border-subtle)",
      background: "var(--color-bg-base)",
      padding: "16px 10px",
      overflowY: "auto",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <button
        type="button"
        onClick={onCompose}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          gap: 8, padding: "10px 12px",
          background: "var(--crm-primary)", color: "#fff",
          border: "none", borderRadius: 8,
          fontSize: 13, fontWeight: 600, cursor: "pointer",
          boxShadow: "0 2px 6px rgba(99,102,241,0.25)",
          transition: "transform 120ms, box-shadow 120ms",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 4px 10px rgba(99,102,241,0.35)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 2px 6px rgba(99,102,241,0.25)";
        }}
      >
        <Mail size={14} /> Compose
      </button>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {FOLDERS.map((f) => {
          const Icon = f.icon;
          const active = value === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => onChange(f.id)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "8px 12px", borderRadius: 8,
                background: active ? "var(--crm-primary-soft)" : "transparent",
                color: active ? "var(--crm-primary)" : "var(--color-text-secondary)",
                border: "none", textAlign: "left", cursor: "pointer",
                fontSize: 13, fontWeight: active ? 600 : 500,
                transition: "background 120ms, color 120ms",
              }}
              onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = "var(--color-bg-muted)"; e.currentTarget.style.color = "var(--color-text-primary)"; } }}
              onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--color-text-secondary)"; } }}
            >
              <Icon size={14} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{f.label}</span>
              {f.id === "INBOX" && inboxUnread > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 999,
                  background: "var(--crm-primary)", color: "#fff",
                }}>
                  {inboxUnread > 99 ? "99+" : inboxUnread}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

// ── thread list ──────────────────────────────────────────────────────────────

function ThreadRow({ thread, selected, onSelect, folder }) {
  const unread = (thread.unreadCount || 0) > 0;
  // Sent folder: the "interesting" party is the recipient, not the sender.
  // For everything else (Inbox, Starred, etc.) the first participant is the
  // counterparty we want surfaced.
  const isOutgoing = folder?.direction === "outgoing";
  const peer = isOutgoing
    ? (thread.participants?.[1] || thread.participants?.[0] || "")
    : (thread.participants?.[0] || "");
  const display = peer.split("@")[0] || peer || "(unknown)";
  // Bookmark = Gmail STARRED label. Server can send either an `isStarred`
  // flag (after we patched it client-side) or the raw `labels` array — fall
  // back to the array check so a fresh thread without our optimistic patch
  // still renders correctly.
  const isStarred = thread.isStarred === true
    || (Array.isArray(thread.labels) && thread.labels.includes("STARRED"));
  const starMut = useMarkThreadStarred();
  const toggleStar = (e) => {
    e.stopPropagation();
    e.preventDefault();
    starMut.mutate(
      { accountId: thread.accountId, threadId: thread._id, isStarred: !isStarred },
      { onError: (err) => toast.error(formatErr(err)) },
    );
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect({ accountId: thread.accountId, threadId: thread._id })}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect({ accountId: thread.accountId, threadId: thread._id });
        }
      }}
      className="crm-inbox-row"
      data-selected={selected ? "true" : "false"}
      style={{
        position: "relative",
        width: "100%",
        textAlign: "left",
        padding: "12px 18px 12px 18px",
        background: selected ? "var(--crm-primary-soft)" : "transparent",
        border: "none",
        borderBottom: "1px solid var(--color-border-subtle)",
        cursor: "pointer",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        transition: "background 120ms",
      }}
    >
      {unread && (
        <span aria-hidden style={{
          position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
          width: 6, height: 6, borderRadius: 999, background: "var(--crm-primary)",
        }} />
      )}
      <div style={{
        width: 32, height: 32, borderRadius: 9999,
        background: tintFor(peer), color: inkFor(peer),
        display: "flex", alignItems: "center", justifyContent: "center",
        fontWeight: 600, fontSize: 11, flexShrink: 0,
      }}>
        {initialsOf(peer)}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
          <div style={{
            fontSize: 13.5,
            fontWeight: unread ? 700 : 500,
            color: "var(--color-text-primary)",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1, letterSpacing: "-0.005em",
          }}>
            {display}
          </div>
          <div style={{
            fontSize: 11, color: "var(--color-text-secondary)",
            flexShrink: 0, fontVariantNumeric: "tabular-nums",
          }}>
            {fmtDate(thread.lastMessageAt)}
          </div>
        </div>
        <div style={{
          fontSize: 13,
          fontWeight: unread ? 600 : 500,
          color: "var(--color-text-primary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          marginTop: 2,
        }}>
          {thread.subject || "(no subject)"}
        </div>
        <div style={{
          fontSize: 12,
          color: "var(--color-text-secondary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          marginTop: 2, lineHeight: 1.4,
        }}>
          {thread.snippet || ""}
        </div>
      </div>
      {/* Bookmark (Gmail STARRED) toggle. Filled gold when starred; empty
          and grayed otherwise. stopPropagation in toggleStar prevents the
          row's onClick from also opening the thread. */}
      <button
        type="button"
        onClick={toggleStar}
        disabled={starMut.isPending}
        title={isStarred ? "Remove star" : "Star this thread"}
        aria-pressed={isStarred}
        style={{
          flexShrink: 0,
          background: "transparent",
          border: "none",
          padding: 4,
          marginLeft: 4,
          marginTop: -2,
          cursor: starMut.isPending ? "wait" : "pointer",
          color: isStarred ? "#F59E0B" : "var(--color-text-secondary)",
          opacity: isStarred ? 1 : 0.55,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Star size={15} fill={isStarred ? "#F59E0B" : "none"} strokeWidth={isStarred ? 1.5 : 2} />
      </button>
    </div>
  );
}

function ThreadList({ threads, selectedKey, onSelect, sentinelRef, hasNext, isFetchingMore, folder, emptyText }) {
  if (!threads.length) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-secondary)" }}>
        <MailOpen size={28} style={{ opacity: 0.4, marginBottom: 10 }} />
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
          {emptyText || "No threads yet"}
        </div>
        <div style={{ fontSize: 11, marginTop: 4 }}>New mail shows up here within a few minutes.</div>
      </div>
    );
  }
  return (
    <div>
      {threads.map((t) => {
        const key = `${t.accountId}:${t._id}`;
        return (
          <ThreadRow
            key={key}
            thread={t}
            selected={key === selectedKey}
            onSelect={onSelect}
            folder={folder}
          />
        );
      })}
      {hasNext && (
        <div
          ref={sentinelRef}
          style={{
            padding: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 8, fontSize: 12, color: "var(--color-text-secondary)",
          }}
        >
          {isFetchingMore ? (
            <>
              <Loader2 size={13} className="animate-spin" /> Loading more…
            </>
          ) : (
            <span style={{ opacity: 0.6 }}>Scroll to load more</span>
          )}
        </div>
      )}
      {!hasNext && threads.length > PAGE_SIZE && (
        <div style={{
          padding: 16, textAlign: "center",
          fontSize: 11, color: "var(--color-text-secondary)", opacity: 0.6,
        }}>
          You've reached the end
        </div>
      )}
    </div>
  );
}

// ── attachment chip ──────────────────────────────────────────────────────────

function AttachmentChipIncoming({ accountId, messageId, att }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await downloadAttachment({
        accountId,
        messageId,
        attachmentId: att.attachmentId,
        filename: att.filename,
        mimeType: att.mimeType,
      });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      title={`Download ${att.filename}`}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "6px 10px 6px 8px",
        background: "var(--color-bg-muted)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 8, fontSize: 12,
        color: "var(--color-text-primary)",
        cursor: busy ? "wait" : "pointer",
        maxWidth: 260,
        transition: "background 120ms, border-color 120ms",
      }}
      onMouseEnter={(e) => {
        if (busy) return;
        e.currentTarget.style.background = "var(--color-bg-surface)";
        e.currentTarget.style.borderColor = "var(--crm-primary)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--color-bg-muted)";
        e.currentTarget.style.borderColor = "var(--color-border-subtle)";
      }}
    >
      <FileText size={13} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>
        {att.filename}
      </span>
      <span style={{ fontSize: 11, color: "var(--color-text-secondary)", flexShrink: 0 }}>
        {fmtBytes(att.size)}
      </span>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} style={{ opacity: 0.6 }} />}
    </button>
  );
}

// ── message body (HTML or text) ──────────────────────────────────────────────

// Render email HTML in a Shadow DOM root so the email's own <style> rules
// stay scoped to itself — no bleeding into the host page. Shadow DOM gives
// us the same isolation as an iframe but without the box-shaped clipping
// and width awkwardness, so marketing templates lay out at full reader
// width like Gmail's own UI.
//
// Trade-off vs iframe: the email's CSS still can't reach our globals, but
// scripts could in theory run. We strip <script> tags + on* attributes +
// javascript: URLs at sanitize time. JS-only "active" content is rare in
// marketing email, but we still belt-and-braces it.
function sanitizeEmailHtml(html) {
  if (!html) return "";
  let out = String(html);
  // Drop <script>…</script> blocks entirely.
  out = out.replace(/<script[\s\S]*?<\/script\s*>/gi, "");
  // Drop <iframe>, <object>, <embed>, <link>.
  out = out.replace(/<(iframe|object|embed|link|meta)\b[^>]*>/gi, "");
  // Strip inline event handlers (onclick, onerror, ...).
  out = out.replace(/\s+on[a-z]+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*'[^']*'/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*[^\s>]+/gi, "");
  // Neutralize javascript: URLs.
  out = out.replace(/(href|src|action)\s*=\s*"\s*javascript:[^"]*"/gi, '$1="#"');
  out = out.replace(/(href|src|action)\s*=\s*'\s*javascript:[^']*'/gi, "$1='#'");
  return out;
}

function HtmlMessageBody({ html, collapsed, onWantExpand }) {
  const hostRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  const COLLAPSED_MAX = 480;
  const isCollapsed = collapsed && contentHeight > COLLAPSED_MAX;

  const sanitized = useMemo(() => sanitizeEmailHtml(html), [html]);

  // Attach a Shadow DOM root once, then re-render the HTML into it whenever
  // it changes. We also inject our own reset / sizing styles into the
  // shadow tree — the email's own <style> stays scoped underneath these.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    let root = host.shadowRoot;
    if (!root) root = host.attachShadow({ mode: "open" });

    // Build the inner content with our base layer + the email body.
    root.innerHTML = `
      <style>
        :host { display: block; width: 100%; }
        .crm-mail-root {
          all: initial;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
          color: inherit;
          font-size: 14px;
          line-height: 1.6;
          display: block;
          width: 100%;
          word-wrap: break-word;
          overflow-wrap: anywhere;
        }
        .crm-mail-root img,
        .crm-mail-root video {
          max-width: 100% !important;
          height: auto !important;
          display: inline-block;
        }
        .crm-mail-root table {
          max-width: 100% !important;
          border-collapse: collapse;
        }
        .crm-mail-root a { color: inherit; text-decoration: underline; }
        .crm-mail-root blockquote {
          margin: 12px 0;
          padding: 4px 12px;
          border-left: 3px solid rgba(127,127,127,0.25);
          color: rgba(127,127,127,0.9);
        }
        .crm-mail-root pre {
          font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
          background: rgba(127,127,127,0.08);
          padding: 10px;
          border-radius: 6px;
          overflow-x: auto;
        }
      </style>
      <div class="crm-mail-root">${sanitized}</div>
    `;

    // All anchor clicks inside the shadow tree → new tab.
    const onClick = (e) => {
      const t = e.target.closest && e.target.closest("a");
      if (t && t.getAttribute("href")) {
        e.preventDefault();
        const href = t.getAttribute("href");
        if (href && href !== "#") window.open(href, "_blank", "noopener,noreferrer");
      }
    };
    root.addEventListener("click", onClick);

    // Track real rendered height for the collapse fade.
    const rootEl = root.querySelector(".crm-mail-root");
    let cancelled = false;
    const timeouts = [];

    function measure() {
      if (cancelled || !rootEl) return;
      const h = rootEl.scrollHeight || rootEl.offsetHeight || 0;
      if (h) setContentHeight((prev) => (Math.abs(prev - h) > 2 ? h : prev));
    }

    measure();
    // Re-measure as remote images load.
    [50, 200, 500, 1000, 2000, 4000].forEach((d) => timeouts.push(setTimeout(measure, d)));
    let ro;
    try {
      ro = new ResizeObserver(measure);
      if (rootEl) ro.observe(rootEl);
    } catch (_) { /* older browsers */ }
    // Hook image load events directly — most reliable signal.
    if (rootEl) {
      rootEl.querySelectorAll("img").forEach((img) => {
        if (img.complete) return;
        img.addEventListener("load", measure, { once: true });
        img.addEventListener("error", measure, { once: true });
      });
    }

    return () => {
      cancelled = true;
      timeouts.forEach((t) => clearTimeout(t));
      ro?.disconnect();
      root.removeEventListener("click", onClick);
    };
  }, [sanitized]);

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{
          maxHeight: isCollapsed ? COLLAPSED_MAX : "none",
          overflow: isCollapsed ? "hidden" : "visible",
          transition: "max-height 200ms ease",
        }}
      >
        <div ref={hostRef} />
      </div>
      {isCollapsed && (
        <div
          onClick={onWantExpand}
          style={{
            position: "absolute", left: 0, right: 0, bottom: 0,
            height: 90,
            background: "linear-gradient(to bottom, transparent, var(--color-bg-surface) 75%)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            paddingBottom: 10,
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        >
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px",
            background: "var(--color-bg-surface)",
            border: "1px solid var(--color-border-subtle)",
            borderRadius: 999,
            fontSize: 12, fontWeight: 600,
            color: "var(--color-text-primary)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}>
            Click to expand
          </span>
        </div>
      )}
    </div>
  );
}

function PlainTextBody({ text }) {
  return (
    <div style={{
      fontSize: 13.5,
      color: "var(--color-text-primary)",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      lineHeight: 1.6,
      letterSpacing: "-0.003em",
    }}>
      {text}
    </div>
  );
}

function MessageBody({ msg }) {
  const [forceText, setForceText] = useState(false);
  // Marketing/newsletter emails are huge — keep them collapsed initially so
  // a thread stays scannable. Click anywhere on the fade to expand.
  const [collapsed, setCollapsed] = useState(true);

  const hasHtml = !!(msg.bodyHtml && msg.bodyHtml.trim());
  const hasText = !!(msg.bodyText && msg.bodyText.trim());

  // Prefer HTML when available — that's the real template the sender designed.
  if (hasHtml && !forceText) {
    return (
      <div>
        <HtmlMessageBody
          html={msg.bodyHtml}
          collapsed={collapsed}
          onWantExpand={() => setCollapsed(false)}
        />
        <div style={{ display: "flex", gap: 12, marginTop: 10, alignItems: "center" }}>
          {!collapsed && (
            <button
              type="button"
              onClick={() => setCollapsed(true)}
              style={linkBtn}
            >
              Collapse
            </button>
          )}
          {hasText && (
            <button
              type="button"
              onClick={() => setForceText(true)}
              style={linkBtn}
            >
              View plain text
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PlainTextBody text={msg.bodyText || msg.snippet || ""} />
      {hasHtml && forceText && (
        <button
          type="button"
          onClick={() => setForceText(false)}
          style={{ ...linkBtn, marginTop: 10 }}
        >
          View rich template
        </button>
      )}
    </div>
  );
}

const linkBtn = {
  padding: 0,
  background: "transparent", border: "none", cursor: "pointer",
  fontSize: 11, fontWeight: 500,
  color: "var(--color-text-secondary)",
  textDecoration: "underline", textUnderlineOffset: 2,
};

// ── single message ───────────────────────────────────────────────────────────

function MessageRecipients({ msg, outbound, tone, IconArrow }) {
  if (outbound) {
    const lines = getRecipientLines(msg, { outbound: true });
    if (!lines.length) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
          <IconArrow size={13} style={{ color: tone, flexShrink: 0 }} />
          <span>To: (none)</span>
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {lines.map(({ label, value }, i) => (
          <div
            key={label}
            style={{
              display: "flex", alignItems: "flex-start", gap: 6,
              fontSize: 13,
              fontWeight: i === 0 ? 600 : 500,
              color: i === 0 ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              lineHeight: 1.4,
            }}
          >
            {i === 0 && <IconArrow size={13} style={{ color: tone, flexShrink: 0, marginTop: 2 }} />}
            {i > 0 && <span style={{ width: 13, flexShrink: 0 }} aria-hidden />}
            <span style={{ minWidth: 32, flexShrink: 0, fontWeight: 600 }}>{label}:</span>
            <span style={{ wordBreak: "break-word" }}>{value}</span>
          </div>
        ))}
      </div>
    );
  }

  const ccLines = getRecipientLines(msg, { outbound: false });
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)" }}>
        <IconArrow size={13} style={{ color: tone }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {msg.fromName || msg.fromAddress}
        </span>
      </div>
      {ccLines.map(({ label, value }) => (
        <div key={label} style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 3, paddingLeft: 19, lineHeight: 1.4 }}>
          <span style={{ fontWeight: 600 }}>{label}:</span> {value}
        </div>
      ))}
    </div>
  );
}

function MessageBlock({ msg, accountId, onReply, onReplyAll, onForward }) {
  const outbound = msg.direction === "outbound";
  const IconArrow = outbound ? ArrowUpRight : ArrowDownLeft;
  const tone = outbound ? "var(--crm-primary)" : "var(--crm-success)";
  const peer = outbound ? (msg.toAddresses?.[0] || "") : (msg.fromAddress || "");
  const hasAttachments = Array.isArray(msg.attachments) && msg.attachments.length > 0;

  return (
    <article style={{
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8,
      background: "var(--color-bg-surface)",
      padding: 16,
      marginBottom: 12,
      boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
    }}>
      <header style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 9999,
          background: tintFor(peer), color: inkFor(peer),
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 600, fontSize: 11, flexShrink: 0,
        }}>
          {initialsOf(peer)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <MessageRecipients msg={msg} outbound={outbound} tone={tone} IconArrow={IconArrow} />
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
            {new Date(msg.sentAt).toLocaleString()}
          </div>
        </div>
      </header>

      <MessageBody msg={msg} />

      {hasAttachments && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--color-border-subtle)" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)",
            marginBottom: 8,
          }}>
            <Paperclip size={11} /> {msg.attachments.length} attachment{msg.attachments.length === 1 ? "" : "s"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {msg.attachments.map((att, i) => (
              <AttachmentChipIncoming
                key={`${att.attachmentId}-${i}`}
                accountId={accountId}
                messageId={msg.providerMessageId}
                att={att}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 8 }}>
        <ActionBtn icon={Reply} label="Reply" onClick={() => onReply(msg)} />
        {onReplyAll && <ActionBtn icon={ReplyAll} label="Reply all" onClick={() => onReplyAll(msg)} />}
        {onForward && <ActionBtn icon={Forward} label="Forward" onClick={() => onForward(msg)} />}
      </div>
    </article>
  );
}

function ActionBtn({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "6px 12px", fontSize: 12, fontWeight: 600,
        background: "transparent", border: "1px solid var(--color-border-subtle)",
        borderRadius: 8, cursor: "pointer", color: "var(--color-text-secondary)",
        transition: "all 120ms",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--crm-primary)"; e.currentTarget.style.color = "var(--crm-primary)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--color-border-subtle)"; e.currentTarget.style.color = "var(--color-text-secondary)"; }}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

// ── thread reader ────────────────────────────────────────────────────────────

function ThreadReader({ accountId, threadId, accountEmail, onReply, onReplyAll, onForward, onMarkedRead, onThreadAction }) {
  const { data, isLoading, isError, error } = useEmailThread(accountId, threadId);
  const markRead = useMarkThreadRead();
  const starMut = useMarkThreadStarred();
  const modifyThread = useModifyThread();
  const markedRef = useRef(null);

  // Auto-mark-read once the thread payload arrives. Guard against re-firing
  // on every refetch by keying off the threadId we last marked.
  useEffect(() => {
    if (!accountId || !threadId || !data) return;
    if (markedRef.current === threadId) return;
    const anyUnread = (data.messages || []).some((m) => !m.isRead);
    markedRef.current = threadId;
    if (anyUnread) {
      markRead.mutate(
        { accountId, threadId, isRead: true },
        { onSuccess: () => onMarkedRead?.(threadId) },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, threadId, data]);

  const markUnread = () => {
    if (!accountId || !threadId) return;
    markRead.mutate(
      { accountId, threadId, isRead: false },
      {
        onSuccess: () => {
          // Allow auto-mark-read to fire again if the user re-opens this thread.
          markedRef.current = null;
          toast.success("Marked unread");
          onMarkedRead?.(threadId, false);
        },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  const runAction = (action, successMsg) => {
    if (!accountId || !threadId || modifyThread.isPending) return;
    modifyThread.mutate(
      { accountId, threadId, action },
      {
        onSuccess: () => {
          toast.success(successMsg);
          onThreadAction?.(action);
        },
        onError: (err) => toast.error(formatErr(err)),
      },
    );
  };

  if (!threadId) {
    return (
      <div style={{
        padding: 64, textAlign: "center", color: "var(--color-text-secondary)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        height: "100%",
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 8,
          background: "var(--color-bg-muted)", color: "var(--color-text-secondary)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          marginBottom: 14,
        }}>
          <Mail size={26} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 500, color: "var(--color-text-primary)" }}>
          Select a conversation
        </div>
        <div style={{ fontSize: 12, marginTop: 4 }}>
          Pick a thread on the left to read it.
        </div>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div style={{ padding: 32, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--color-text-secondary)", height: "100%" }}>
        <Loader2 size={14} className="animate-spin" /> Loading thread…
      </div>
    );
  }
  if (isError) {
    return (
      <div style={{ margin: 18, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 12, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
        <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 13 }}>{formatErr(error)}</div>
      </div>
    );
  }
  const thread = data?.thread;
  const messages = data?.messages || [];
  const isStarred = !!thread?.isStarred;
  const toggleStar = () => {
    if (!accountId || !threadId || starMut.isPending) return;
    starMut.mutate(
      { accountId, threadId, isStarred: !isStarred },
      { onError: (err) => toast.error(formatErr(err)) },
    );
  };
  return (
    <div style={{ padding: "20px 28px", overflowY: "auto", height: "100%" }}>
      <div style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 12, marginBottom: 20,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{
            margin: 0, fontSize: 20, fontWeight: 600,
            letterSpacing: "-0.01em", lineHeight: 1.3,
            color: "var(--color-text-primary)",
          }}>
            {thread?.subject || "(no subject)"}
          </h2>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 6 }}>
            {messages.length} {messages.length === 1 ? "message" : "messages"}
          </div>
        </div>
        <div style={{ display: "inline-flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          <ActionBtn icon={Archive} label="Archive" onClick={() => runAction("archive", "Archived")} />
          <ActionBtn icon={Trash2} label="Delete" onClick={() => runAction("trash", "Moved to trash")} />
          <ActionBtn icon={AlertCircle} label="Spam" onClick={() => runAction("spam", "Marked as spam")} />
          <button
            type="button"
            onClick={toggleStar}
            disabled={starMut.isPending}
            title={isStarred ? "Remove star" : "Star this thread"}
            aria-pressed={isStarred}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 10px", fontSize: 12, fontWeight: 600,
              background: isStarred ? "rgba(245, 158, 11, 0.12)" : "transparent",
              border: `1px solid ${isStarred ? "#F59E0B" : "var(--color-border-subtle)"}`,
              borderRadius: 8, cursor: starMut.isPending ? "wait" : "pointer",
              color: isStarred ? "#F59E0B" : "var(--color-text-secondary)",
              transition: "all 120ms",
            }}
          >
            {starMut.isPending
              ? <Loader2 size={12} className="animate-spin" />
              : <Star size={12} fill={isStarred ? "#F59E0B" : "none"} strokeWidth={isStarred ? 1.5 : 2} />}
            {isStarred ? "Starred" : "Star"}
          </button>
          <button
            type="button"
            onClick={markUnread}
            disabled={markRead.isPending}
            title="Mark as unread"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "6px 10px", fontSize: 12, fontWeight: 600,
              background: "transparent", border: "1px solid var(--color-border-subtle)",
              borderRadius: 8, cursor: "pointer", color: "var(--color-text-secondary)",
              transition: "all 120ms",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--crm-primary)"; e.currentTarget.style.color = "var(--crm-primary)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--color-border-subtle)"; e.currentTarget.style.color = "var(--color-text-secondary)"; }}
          >
            {markRead.isPending ? <Loader2 size={12} className="animate-spin" /> : <MailMinus size={12} />}
            Mark unread
          </button>
        </div>
      </div>
      {messages.map((m) => (
        <MessageBlock
          key={m._id}
          msg={m}
          accountId={accountId}
          onReply={(msg) => onReply(msg, accountEmail)}
          onReplyAll={(msg) => onReplyAll?.(msg, accountEmail)}
          onForward={(msg) => onForward?.(msg, accountEmail)}
        />
      ))}
    </div>
  );
}

// ── main screen ──────────────────────────────────────────────────────────────

export default function Inbox() {
  const { data: accounts = [], isLoading: accountsLoading } = useEmailAccounts();
  const activeAccounts = useMemo(() => accounts.filter((a) => a.status === "active"), [accounts]);

  const [accountId, setAccountId] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [folderId, setFolderId] = useState("INBOX");
  const folder = useMemo(() => FOLDERS.find((f) => f.id === folderId) || FOLDERS[0], [folderId]);
  // "ALL" is a UI affordance — backend gets undefined so it returns all mail.
  const effectiveLabel = folderId === "ALL" ? undefined : folderId;

  useEffect(() => {
    if (!accountId && activeAccounts.length) setAccountId(String(activeAccounts[0]._id));
  }, [accountId, activeAccounts]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Clear the selected thread when the folder switches — the previously
  // opened thread may not exist in the new label set.
  useEffect(() => { setSelected(null); }, [folderId]);

  const {
    data, isLoading, isError, error, refetch,
    fetchNextPage, hasNextPage, isFetchingNextPage,
  } = useEmailThreads({
    accountId,
    q: debouncedQ || undefined,
    limit: PAGE_SIZE,
    labelId: effectiveLabel,
    refetchInterval: 45_000,
  });

  const { data: mailboxStats } = useMailboxStats(accountId);
  const inboxUnread = mailboxStats?.inboxUnread || 0;

  // Flatten paginated pages into a single thread list for the UI.
  const threads = useMemo(
    () => (data?.pages || []).flatMap((p) => p?.threads || []),
    [data],
  );

  // IntersectionObserver — when the sentinel at the bottom of the list scrolls
  // into view, fetch the next page. Disabled while a fetch is in flight or
  // when we've exhausted Gmail's pageToken cursor.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    const io = new IntersectionObserver((entries) => {
      const e = entries[0];
      if (e?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }, { rootMargin: "200px 0px" });
    io.observe(node);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, threads.length]);

  const sync = useManualSync();
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState("new");
  const [replyTo, setReplyTo] = useState(null);

  const currentAccountEmail = useMemo(
    () => activeAccounts.find((a) => String(a._id) === String(accountId))?.email || "",
    [activeAccounts, accountId],
  );

  const buildReplyPayload = (msg, accEmail) => ({
    accountId,
    accountEmail: accEmail || currentAccountEmail,
    subject: msg.subject,
    fromAddress: msg.fromAddress,
    toAddresses: msg.toAddresses,
    ccAddresses: msg.ccAddresses,
    bccAddresses: msg.bccAddresses,
    replyTo: {
      providerMessageId: msg.providerMessageId,
      providerThreadId: msg.providerThreadId,
      inReplyTo: msg.rfcMessageId || msg.providerMessageId,
      references: [...(msg.references || []), msg.rfcMessageId].filter(Boolean),
      subject: msg.subject,
    },
  });

  const onReply = (msg, accEmail) => {
    setComposeMode("reply");
    setReplyTo(buildReplyPayload(msg, accEmail));
    setComposeOpen(true);
  };

  const onReplyAll = (msg, accEmail) => {
    setComposeMode("replyAll");
    setReplyTo(buildReplyPayload(msg, accEmail));
    setComposeOpen(true);
  };

  const onForward = (msg, accEmail) => {
    setComposeMode("forward");
    setReplyTo({
      accountId,
      accountEmail: accEmail || currentAccountEmail,
      message: msg,
    });
    setComposeOpen(true);
  };

  const openCompose = () => {
    setComposeMode("new");
    setReplyTo(null);
    setComposeOpen(true);
  };

  const onSync = async () => {
    if (!accountId) return;
    try {
      await sync.mutateAsync(accountId);
      toast.success("Synced");
      refetch();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  if (accountsLoading) {
    return (
      <div style={{ padding: 56, display: "flex", alignItems: "center", justifyContent: "center", gap: 10, color: "var(--color-text-secondary)" }}>
        <Loader2 size={18} className="animate-spin" /> Loading…
      </div>
    );
  }

  if (activeAccounts.length === 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <PageHeader title="Email" subtitle="Connect a mailbox to see conversations here" />
        <ConnectMailboxCTA />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <style>{`
        .crm-inbox-row:hover { background: var(--color-bg-muted) !important; }
        .crm-inbox-row[data-selected="true"]:hover { background: var(--crm-primary-soft) !important; }
      `}</style>

      <PageHeader
        title={folder.label}
        actions={
          <Button variant="secondary" size="sm" onClick={onSync} disabled={sync.isPending}
            leftIcon={sync.isPending ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}>
            Sync
          </Button>
        }
        topRight={<>
          <label className="tg-tool tg-tool-search" style={{ maxWidth: 260 }}>
            <Search size={13} />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search subject, sender, snippet…"
            />
          </label>
          <span className="tg-tool-sep" />
          <AntSelect
            value={accountId || undefined}
            onChange={(v) => { setAccountId(v); setSelected(null); }}
            options={activeAccounts.map((a) => ({ value: a._id, label: a.email }))}
            style={{ minWidth: 200, height: 28 }}
          />
        </>}
      />

      <div style={{ flex: 1, minHeight: 0, display: "flex", marginTop: 12 }}>
        <FolderRail
          value={folderId}
          onChange={setFolderId}
          onCompose={openCompose}
          inboxUnread={inboxUnread}
        />
        <div style={{
          width: 400,
          borderRight: "1px solid var(--color-border-subtle)",
          overflowY: "auto",
          background: "var(--color-bg-surface)",
        }}>
          {isLoading && (
            <div style={{ padding: 32, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: "var(--color-text-secondary)" }}>
              <Loader2 size={14} className="animate-spin" /> Loading threads…
            </div>
          )}
          {isError && (
            <div style={{ margin: 16, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 12, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 13 }}>{formatErr(error)}</div>
            </div>
          )}
          {!isLoading && !isError && (
            <ThreadList
              threads={threads}
              selectedKey={selected ? `${selected.accountId}:${selected.threadId}` : null}
              onSelect={setSelected}
              sentinelRef={sentinelRef}
              hasNext={hasNextPage}
              isFetchingMore={isFetchingNextPage}
              folder={folder}
              emptyText={`No ${folder.label.toLowerCase()} threads`}
            />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0, overflowY: "auto", background: "var(--color-bg-base)" }}>
          <ThreadReader
            accountId={selected?.accountId}
            threadId={selected?.threadId}
            accountEmail={currentAccountEmail}
            onReply={onReply}
            onReplyAll={onReplyAll}
            onForward={onForward}
            onMarkedRead={() => refetch()}
            onThreadAction={() => { setSelected(null); refetch(); }}
          />
        </div>
      </div>

      <EmailComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        mode={composeMode}
        replyTo={replyTo}
      />
    </div>
  );
}

